import { ModuleValidationErrors } from "@src/features/libraryItem/module/contexts/ModuleBuilderContext";
import {
  courseHasValidationErrors,
  courseValidationErrorCount,
  validateDraftCourse,
} from "@src/features/libraryItem/course/utils/publishValidation";
import {
  hasSkillValidationErrors,
  skillValidationErrorCount,
  validateSkillVersion,
} from "@src/features/libraryItem/skill/utils/validation";
import { ValidateModule_ModuleVersionFragment } from "@src/features/library/modules/PublishModuleDialog.generated";

export const moduleHasContentValidationErrors = (
  validationErrors: ModuleValidationErrors,
): boolean => {
  return moduleContentValidationErrorCount(validationErrors) > 0;
};

export const moduleContentValidationErrorCount = (
  validationErrors: ModuleValidationErrors,
): number => {
  return (
    moduleCourseValidationErrorCount(validationErrors) +
    moduleCourseOutlineValidationErrorCount(validationErrors) +
    moduleSkillValidationErrorCount(validationErrors)
  );
};

export const moduleCourseValidationErrorCount = (
  validationErrors: ModuleValidationErrors,
): number => {
  return Array.from(
    validationErrors.courseIdToValidationErrors.values(),
  ).reduce((acc, courseValidationErrors) => {
    return acc + courseValidationErrorCount(courseValidationErrors);
  }, 0);
};

export const moduleCourseOutlineValidationErrorCount = (
  validationErrors: ModuleValidationErrors,
): number => {
  return validationErrors.courseOutlineIds.length;
};

const moduleSkillValidationErrorCount = (
  validationErrors: ModuleValidationErrors,
): number => {
  return Array.from(validationErrors.skillIdToValidationErrors.values()).reduce(
    (acc, skillValidationErrors) => {
      return acc + skillValidationErrorCount(skillValidationErrors);
    },
    0,
  );
};

export const validateModule = (
  pv: ValidateModule_ModuleVersionFragment,
): ModuleValidationErrors => {
  const newValidationErrors: ModuleValidationErrors = {
    ...emptyModuleValidationErrors(),
    duplicateMembershipUuids: getMembershipUuidsWithDuplicateContent(pv),
  };
  let prevMem = null;
  for (const mem of pv.moduleContentMemberships) {
    if (mem.course) {
      const newCourseValidationErrors = validateDraftCourse(
        mem.course.draftVersion,
      ).validationErrors;
      if (courseHasValidationErrors(newCourseValidationErrors)) {
        newValidationErrors.courseIdToValidationErrors.set(
          mem.course.id,
          validateDraftCourse(mem.course.draftVersion).validationErrors,
        );
      }
    } else if (mem.courseOutline) {
      newValidationErrors.courseOutlineIds.push(mem.courseOutline.id);
    } else if (mem.skill?.draftVersion) {
      const newSkillValidationErrors = validateSkillVersion(
        mem.skill.draftVersion,
      );
      if (hasSkillValidationErrors(newSkillValidationErrors)) {
        newValidationErrors.skillIdToValidationErrors.set(
          mem.skill.id,
          newSkillValidationErrors,
        );
      }
    } else if (mem.sectionBreak) {
      if (prevMem?.sectionBreak) {
        newValidationErrors.emptySectionBreakUuids.add(
          prevMem.sectionBreak.uuid,
        );
      }
    }
    prevMem = mem;
  }
  const lastMem =
    pv.moduleContentMemberships[pv.moduleContentMemberships.length - 1];
  if (lastMem?.sectionBreak) {
    newValidationErrors.emptySectionBreakUuids.add(lastMem.sectionBreak.uuid);
  }
  return newValidationErrors;
};

const getMembershipUuidsWithDuplicateContent = (
  moduleVersion: ValidateModule_ModuleVersionFragment,
): Set<string> => {
  const ret = new Set<string>();
  const seenCourseIds = new Set<number>();
  const seenSkillIds = new Set<number>();
  const seenFileIds = new Set<string>();
  for (const mem of moduleVersion.moduleContentMemberships) {
    if (mem.course) {
      if (seenCourseIds.has(mem.course.id)) {
        ret.add(mem.uuid);
      }
      seenCourseIds.add(mem.course.id);
    } else if (mem.skill) {
      if (seenSkillIds.has(mem.skill.id)) {
        ret.add(mem.uuid);
      }
      seenSkillIds.add(mem.skill.id);
    } else if (mem.trainingResource) {
      if (seenFileIds.has(mem.trainingResource.id)) {
        ret.add(mem.uuid);
      }
      seenFileIds.add(mem.trainingResource.id);
    }
  }
  return ret;
};

export const emptyModuleValidationErrors = (): ModuleValidationErrors => {
  return {
    skillIdToValidationErrors: new Map(),
    courseIdToValidationErrors: new Map(),
    duplicateMembershipUuids: new Set(),
    courseOutlineIds: [],
    emptySectionBreakUuids: new Set(),
  };
};
